import Component from '@glimmer/component';
import Popover from 'tio-reimagined/components/popover';
import type { PopoverPlacement } from 'tio-reimagined/components/popover';

/**
 * Inline text with a hover-revealed tooltip showing the full value.
 *
 * Thin wrapper around the design-system Popover (`@triggerMode="hover"`)
 * so call sites stay one line:
 *
 *   <TooltipText @label={{field.label}} class="truncate">
 *     {{field.label}}
 *   </TooltipText>
 *
 * Use when truncated text needs an overflow-safe, viewport-aware tooltip.
 * Renders a `<span>` and forwards `...attributes` so consumers can style
 * the trigger (e.g. `truncate flex-1 text-left`) without extra wrappers.
 */
export interface TooltipTextSignature {
  Args: {
    /** Full text shown in the tooltip body */
    label: string;
    /** Tooltip placement relative to the trigger @default "top" */
    placement?: PopoverPlacement;
  };
  Blocks: {
    /** Visible (likely truncated) trigger content */
    default: [];
  };
  Element: HTMLSpanElement;
}

export default class TooltipText extends Component<TooltipTextSignature> {
  get placement(): PopoverPlacement {
    return this.args.placement ?? 'top';
  }

  <template>
    <Popover @placement={{this.placement}} @triggerMode="hover" @maxWidth={{360}}>
      <:trigger as |t|>
        <span aria-describedby={{t.panelId}} {{t.register}} {{t.hoverHandlers}} ...attributes>
          {{yield}}
        </span>
      </:trigger>
      <:body>
        <p class="text-sm">{{@label}}</p>
      </:body>
    </Popover>
  </template>
}
